/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react'
import PropTypes from 'prop-types'
import { Button, Card } from '@pareto-engineering/design-system'
import styleNames from '@pareto-engineering/bem/exports'
import { useFormikContext } from 'formik'
import ReactGA from 'react-ga4'
import { useConversionForm } from 'modules/components'

// Local Definitions

const baseClassName = styleNames.base
const componentClassName = 'navigator'

/**
 * This is the component description.
 */
const Navigator = ({
  id,
  className:userClassName,
  wrapperAs:NavigationWrapper,
  style,
  previousColor,
  disabledColor,
  nextColor,
  overrideHideNext,
  overrideHideBack,
}) => {
  const { values } = useFormikContext()
  const {
    canProceed,
    sectionMap,
    currentSectionIndex,
    setPrevSectionIndex,
    setNextSectionIndex,
    currentSection,
    commit,
    isInFlight,
  } = useConversionForm()

  const { hideNext, hideBack } = currentSection

  const hideNextButton = typeof overrideHideNext !== 'undefined'
    ? overrideHideNext
    : hideNext

  const hideBackButton = typeof overrideHideBack !== 'undefined'
    ? overrideHideBack
    : hideBack

  if (hideNextButton && hideNextButton) {
    return null
  }

  const isFirstStep = currentSectionIndex === 0
  const isLastStep = currentSectionIndex === sectionMap.length - 1

  const gaBaseData = {
    category:'Conversion Form Event',
    label   :`Step ${currentSectionIndex + 1}: ${currentSection.title}`,
  }

  const sendGAEvent = (action) => {
    ReactGA.event({
      action,
      ...gaBaseData,
    })
  }

  const clickedNext = () => {
    const nextSectionIsLast = currentSectionIndex + 1 === sectionMap.length - 1
    const action = nextSectionIsLast ? 'Completed Form' : 'Clicked Next'
    if (currentSection.save) {
      commit(values)
    } else {
      setNextSectionIndex(1)
    }
    sendGAEvent(action)
  }

  const clickedPrevious = () => {
    setPrevSectionIndex(1)
    sendGAEvent('Click Back')
  }

  return (
    <NavigationWrapper
      id={id}
      className={[
        baseClassName,
        componentClassName,
        userClassName,
      ]
        .filter((e) => e)
        .join(' ')}
      style={style}
    >
      <Button
        disabled={isFirstStep || isInFlight}
        className={`x-${isFirstStep ? disabledColor : previousColor} next ${hideBackButton ? 'hidden' : ''}`}
        isSimple
        onClick={clickedPrevious}
        arrowDirection="left"
      >
        Back
      </Button>
      <Button
        className={`x-${nextColor} next ${(hideNextButton || isLastStep) ? 'hidden' : ''}`}
        disabled={!canProceed || isInFlight}
        isLoading={isInFlight}
        onClick={clickedNext}
        arrowDirection="right"
      >
        Next
      </Button>
    </NavigationWrapper>
  )
}

Navigator.propTypes = {
  /**
   * The HTML id for this element
   */
  id:PropTypes.string,

  /**
   * The HTML class names for this element
   */
  className:PropTypes.string,

  /**
   * The node to display the the navigation wrapper with
   */
  wrapperAs:PropTypes.node,

  /**
   * The React-written, css properties for this element.
   */
  style:PropTypes.objectOf(PropTypes.string),

  /**
   * The previous color
   */
  previousColor:PropTypes.string,

  /**
   * The color for disabled inputs
   */
  disabledColor:PropTypes.string,
  /**
   * The next color
   */
  nextColor    :PropTypes.string,
}

Navigator.defaultProps = {
  previousColor:'title',
  disabledColor:'disabled',
  nextColor    :'interactive',
  wrapperAs    :Card.Section,
}

export default Navigator
